<script setup lang="ts">
import BreadCrumb, { Page } from "@/components/BreadCrumb.vue";
import useAuthUser from "@/composables/useAuthUser";
import useHasPermissionUser from "@/composables/useHasPermissionUser";
import router from "@/router";
import { ref, watch } from "vue";
import { RouterLink } from "vue-router";
import { ProviderElement } from "../../interfaces/ProviderElement";

const { hasPermissionUser, abilities, redirectTo404 } = useHasPermissionUser();

const pages = ref<Page[]>([
  {
    name: "Proveedores de hoteleria",
    to: "/internal/providers/hotel",
  },
]);

const providers = ref<ProviderElement[]>([
  {
    name: "Cadenas",
    icon: "link",
    to: { name: "hotel.chain" },
    description: "Gestiona las cadenas hoteleras y sus marcas asociadas",
    namePermission: "viewAny-internal",
    entityType: "App\\Models\\Chain",
  },
  {
    name: "Marcas",
    icon: "branding_watermark",
    to: { name: "hotel.brands" },
    description: "Administra las marcas de cada cadena hotelera",
    namePermission: "viewAny-internal",
    entityType: "App\\Models\\Brand",
  },
  {
    name: "Grupos",
    icon: "workspaces",
    to: { name: "hotel.groups" },
    description: "Organiza hoteles en grupos para tarifas especiales",
    namePermission: "viewAny-internal",
    entityType: "App\\Models\\Group",
  },
  {
    name: "Hoteles",
    icon: "apartment",
    to: { name: "hotel.hotels" },
    description: "Catálogo completo de hoteles y sus configuraciones",
    namePermission: "viewAny-internal",
    entityType: "App\\Models\\Hotel",
  },
]);

const iconColors: Record<string, { bg: string; text: string; border: string }> = {
  link: { bg: "bg-blue-50", text: "text-blue-600", border: "border-blue-200" },
  branding_watermark: { bg: "bg-purple-50", text: "text-purple-600", border: "border-purple-200" },
  workspaces: { bg: "bg-emerald-50", text: "text-emerald-600", border: "border-emerald-200" },
  apartment: { bg: "bg-amber-50", text: "text-amber-600", border: "border-amber-200" },
};

watch(abilities, () => {
  if (
    (providers.value = providers.value.filter((provider) =>
      hasPermissionUser(provider.namePermission, provider.entityType),
    ))
  ) {
    return router.push({
      path: "/error/not-found",
    });
  }
});
</script>

<template>
  <BreadCrumb :pages="pages" />

  <main class="mx-auto max-w-7xl px-4 py-8">
    <!-- Header -->
    <div class="mb-8">
      <h1 class="text-3xl font-bold text-gray-900">Proveedores de Hotelería</h1>
      <p class="mt-2 text-gray-600">
        Administra cadenas, marcas, grupos y hoteles desde un solo lugar
      </p>
    </div>

    <!-- Cards Grid -->
    <div class="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
      <template
        v-for="provider in providers.filter((provider) =>
          hasPermissionUser(provider.namePermission, provider.entityType),
        )"
        :key="provider.name"
      >
        <RouterLink
          :to="provider.to"
          class="group relative flex flex-col overflow-hidden rounded-2xl border bg-white p-6 shadow-sm transition-all duration-300 hover:shadow-lg hover:border-gray-300"
        >
          <!-- Icon Container -->
          <div
            :class="[
              'flex h-14 w-14 items-center justify-center rounded-xl border transition-transform duration-300 group-hover:scale-110',
              iconColors[provider.icon]?.bg || 'bg-gray-50',
              iconColors[provider.icon]?.border || 'border-gray-200',
            ]"
          >
            <span
              :class="[
                'material-symbols-outlined text-3xl',
                iconColors[provider.icon]?.text || 'text-gray-600',
              ]"
            >
              {{ provider.icon }}
            </span>
          </div>

          <!-- Content -->
          <div class="mt-5 flex-1">
            <h3 class="text-lg font-semibold text-gray-900 group-hover:text-blue-600 transition-colors">
              {{ provider.name }}
            </h3>
            <p class="mt-2 text-sm text-gray-500 leading-relaxed">
              {{ provider.description }}
            </p>
          </div>

          <!-- Arrow indicator -->
          <div class="mt-4 flex items-center text-sm font-medium text-blue-600 opacity-0 transition-opacity duration-300 group-hover:opacity-100">
            <span>Ver más</span>
            <span class="material-symbols-outlined ml-1 text-lg transition-transform duration-300 group-hover:translate-x-1">
              arrow_forward
            </span>
          </div>

          <!-- Decorative gradient on hover -->
          <div class="absolute inset-x-0 bottom-0 h-1 bg-gradient-to-r from-blue-500 to-blue-600 transform scale-x-0 transition-transform duration-300 group-hover:scale-x-100"></div>
        </RouterLink>
      </template>
    </div>
  </main>
</template>
